<template>
  <span>
    <CommonDialog
      :dialog="dialog"
      :head-text="label || placeholder"
      :on-confirm="
        () => {
          dialog = false;
        }
      "
      :cancel-button="null"
    >
      <v-text-field v-model="search" label="Search" clearable></v-text-field>
      <v-treeview
        :active.sync="item"
        :items="itemsForTree"
        :search="search"
        open-all
        expand-icon="mdi-chevron-down"
        dense
        ref="tree"
        class="tree"
        on-icon="mdi-check"
        activatable
        return-object
        selection-type="independent"
      >
        <template v-slot:prepend="{ item, open }">
          <v-icon v-if="item.icon">
            {{ item.icon }}
          </v-icon>
        </template>
        <template v-slot:label="{ item }">
          <span>{{ item.name }}</span>
          <span
            v-if="item.serial_number"
            style="padding-left: 10px; color: rgba(0, 0, 0, 0.38)"
            >{{ item.serial_number }}</span
          >
        </template>
      </v-treeview>
    </CommonDialog>
    <v-text-field
      readonly
      :value="text"
      :label="label"
      :placeholder="placeholder"
      :loading="loading"
      :rules="rules"
      v-on:click="dialog = true"
      :clearable="clearable"
      @click:clear="handleClear()"
    ></v-text-field>
  </span>
</template>

<script>
import CommonDialog from "../dialogs/CommonDialog";
export default {
  name: "TreeSelector",
  components: { CommonDialog },
  model: {
    prop: "value",
    event: "changeValue"
  },
  props: {
    value: {
      type: [String, Number],
      default: function() {
        return "";
      }
    },
    items: {
      type: Array,
      default: function() {
        return [];
      }
    },
    loading: {
      type: Boolean,
      default: function() {
        return false;
      }
    },
    rules: {
      type: Array,
      default: function() {
        return [];
      }
    },
    label: {
      type: String,
      default: function() {
        return "";
      }
    },
    placeholder: {
      type: String,
      default: function() {
        return "";
      }
    },
    clearable: {
      type: Boolean,
      default: function() {
        return false;
      }
    },
    optionsForTreeItems: {
      type: Object,
      default: function() {
        return {};
      }
    }
  },
  data: () => ({
    dialog: false,
    search: null,
    item: []
  }),
  methods: {
    handleClear() {
      this.$emit("clear-click");
    }
  },
  computed: {
    text() {
      if (this.item[0] && this.item[0].id) {
        let item = this.items.find(i => i.id === this.item[0].id) || {};
        return [
          ...(item[this.optionsForTreeItems.groupKey || "_group"] || []).map(
            i => i.name
          ),
          item.name
        ].join(" > ");
      } else {
        return "";
      }
    },
    itemsForTree() {
      let items = this.items;
      let options = this.optionsForTreeItems;
      let result = { children: [] };
      options = {
        groupKey: "_group",
        getItem: i => ({
          id: i.id,
          name: i.name,
          serial_number: i.serial_number,
          icon: i.icon,
          alwaysActive: i.alwaysActive
        }),
        getItemGroup: i => ({
          id: i.id,
          name: i.name,
          serial_number: i.serial_number,
          icon: i.icon,
          alwaysActive: i.alwaysActive
        }),
        setItem: i => ({
          id: i.id,
          name: i.name,
          serial_number: i.serial_number,
          icon: i.icon,
          alwaysActive: i.alwaysActive
        }),
        canChooseGroup: true,
        ...options
      };

      items.forEach(item => {
        let cursor = result;
        [
          ...(item[options.groupKey] || []).reduce((ac, i) => {
            ac.push(options.getItemGroup(i));
            return ac;
          }, []),
          options.getItem(item)
        ].forEach(i => {
          cursor.disabled = !cursor.alwaysActive && !options.canChooseGroup;
          let index = cursor.children.findIndex(u => i.id === u.id);
          if (index === -1) {
            index =
              cursor.children.push({
                ...options.setItem(i),
                disabled: false,
                children: []
              }) - 1;
          } else {
            cursor.children[index] = {
              ...cursor.children[index],
              ...options.setItem(i)
            };
          }
          cursor.children[index].disabled =
            !cursor.children[index].alwaysActive &&
            cursor.children[index].disabled;
          cursor = cursor.children[index];
        });
        return item;
      });

      function sort(item) {
        (item.children || []).forEach(i => sort(i));

        (item.children || []).sort(function(a, b) {
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        });
      }
      sort(result);

      return result.children;
    }
  },
  watch: {
    item(val) {
      this.$emit("changeValue", (val[0] || {}).id || "");
    },
    value(val) {
      if (val !== (this.item[0] || {}).id) {
        this.item = [{ id: val }];
      }
    },
    items() {
      if (this.items.findIndex(i => i.id === (this.item[0] || {}).id) === -1) {
        this.item = [];
      }
    }
  },
  mounted() {
    this.item = [{ id: this.value }];
  }
};
</script>

<style lang="scss" scoped>
.tree::v-deep {
  max-height: 60vh;
  overflow: auto;
  .v-treeview-node__prepend {
    .v-icon {
      color: inherit;
    }
    &:empty {
      display: none;
    }
  }
  .v-treeview-node {
    cursor: pointer;
    &.v-treeview-node--disabled {
      cursor: auto;
    }
    &:not(.v-treeview-node--disabled) .v-treeview-node__root:hover::before {
      opacity: 0.04;
      color: rgba(0, 0, 0, 0.87) !important;
      transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
    }
  }
  .v-treeview-node--active {
    font-weight: 600;
    &::before {
      opacity: 0.12 !important;
      color: rgba(0, 0, 0, 0.87) !important;
    }
  }
  .theme--light.v-icon:focus:after {
    opacity: 0;
  }
  .v-treeview-node__toggle {
    transform: rotate(0);
    &.v-treeview-node__toggle--open {
      transform: rotate(-180deg);
    }
  }
}
</style>
